import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Loader2, RotateCcw, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { api } from '@/lib/api/client';
import { useQuery } from '@tanstack/react-query';

interface CliLogRow {
  id: number;
  userId: number;
  username?: string;
  userType?: string;
  parentId?: number;
  cliId: number;
  cliValue?: string;
  action?: 'ALLOCATED' | 'REMOVED';
  actionAt?: string;
  targetUserId?: number;
  targetUsername?: string;
}

const fmt = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export function CliLogsPage() {
  const today = new Date();
  // Live picker values — free to change without triggering a fetch.
  const [startDate, setStartDate] = useState<Date>(today);
  const [endDate, setEndDate] = useState<Date>(today);
  // Drives the query. Starts at today so the current date's logs load by
  // default on page open; after that, only "Search" (or "Reset") updates it.
  const [appliedRange, setAppliedRange] = useState<{ start: Date; end: Date } | null>({
    start: today,
    end: today,
  });

  const logsQuery = useQuery({
    queryKey: [
      'cli-logs',
      'list',
      appliedRange ? fmt(appliedRange.start) : null,
      appliedRange ? fmt(appliedRange.end) : null,
    ],
    queryFn: async () => {
      const url = `/api/obd/cli/logs?startDate=${fmt(appliedRange!.start)}&endDate=${fmt(appliedRange!.end)}`;
      const res = await api.get<CliLogRow[]>(url);
      return Array.isArray(res) ? res : [];
    },
    enabled: appliedRange !== null,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const logs = logsQuery.data ?? [];

  const columns = useMemo<ColumnDef<CliLogRow>[]>(
    () => [
      {
        accessorKey: 'id',
        header: '#',
        size: 70,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.id}</span>
        ),
      },
      { accessorKey: 'username', header: 'Performed By' },
      { accessorKey: 'targetUsername', header: 'CLI User' },
      {
        accessorKey: 'targetUserId',
        header: 'User ID',
        size: 100,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.targetUserId ?? '—'}</span>
        ),
      },
      {
        accessorKey: 'parentId',
        header: 'Parent ID',
        size: 100,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.parentId ?? '—'}</span>
        ),
      },
      {
        accessorKey: 'cliId',
        header: 'CLI ID',
        size: 100,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.cliId}</span>
        ),
      },
      {
        accessorKey: 'cliValue',
        header: 'CLI Number',
        cell: ({ row }) => (
          <span className="font-medium">{row.original.cliValue ?? '—'}</span>
        ),
      },
      {
        accessorKey: 'action',
        header: 'Action',
        size: 110,
        cell: ({ row }) => (
          <Badge variant={row.original.action === 'REMOVED' ? 'destructive' : 'secondary'}>
            {row.original.action === 'REMOVED' ? 'Deallocated' : 'Allocated'}
          </Badge>
        ),
      },
      {
        accessorKey: 'actionAt',
        header: 'Date',
        size: 190,
      },
    ],
    [],
  );

  const handleSearch = () => setAppliedRange({ start: startDate, end: endDate });
  const handleReset = () => {
    const t = new Date();
    setStartDate(t);
    setEndDate(t);
    setAppliedRange({ start: t, end: t });
  };

  return (
    <FillPage>
      <PageHeader
        title="CLI Logs"
        description="Track CLI allocations and removals."
      />

      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
          <CardDescription>Narrow logs by date range.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-44">
              <label className="mb-1 block text-sm font-medium">Start date</label>
              <DatePicker
                value={startDate}
                onChange={(d) => d && setStartDate(d)}
                toDate={new Date()}
              />
            </div>
            <div className="w-44">
              <label className="mb-1 block text-sm font-medium">End date</label>
              <DatePicker
                value={endDate}
                onChange={(d) => d && setEndDate(d)}
                fromDate={startDate}
                toDate={new Date()}
              />
            </div>

            <Button onClick={handleSearch} disabled={logsQuery.isFetching}>
              {logsQuery.isFetching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              Search
            </Button>
            <Button variant="outline" onClick={handleReset} disabled={logsQuery.isFetching}>
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <DataTable<CliLogRow, unknown>
        tableId="cli-logs:v3"
        columns={columns}
        data={logs}
        isLoading={logsQuery.isFetching}
        searchPlaceholder="Search by username, CLI..."
        emptyTitle="No CLI logs"
        emptyDescription="No CLI allocation or removal logs found for the selected filters."
        getRowId={(row) => String(row.id)}
        exportFilename={`cli-logs-${fmt(new Date())}.csv`}
        pageSize={25}
      />
    </FillPage>
  );
}